/**
 * Frontend password policy — mirrors the backend `strongPassword` schema at
 * [server/lib/passwordPolicy.ts](../../server/lib/passwordPolicy.ts).
 *
 * Any bump to length or complexity rules must be kept in sync on both sides.
 * There is no shared TS file between client and server in this repo, so the
 * two modules are intentional duplicates of a small, rarely-changing rule set.
 */

import { t } from './i18n';

export const MIN_PASSWORD_LENGTH = 12;

export interface PasswordCheck {
  /** Translated, rendered lazily so locale switches update the label. */
  label: () => string;
  test: (pw: string) => boolean;
}

export const PASSWORD_CHECKS: PasswordCheck[] = [
  { label: () => t(`At least ${MIN_PASSWORD_LENGTH} characters`), test: (p) => p.length >= MIN_PASSWORD_LENGTH },
  { label: () => t('One lowercase letter'), test: (p) => /[a-z]/.test(p) },
  { label: () => t('One uppercase letter'), test: (p) => /[A-Z]/.test(p) },
  { label: () => t('One number'), test: (p) => /[0-9]/.test(p) },
  { label: () => t('One symbol'), test: (p) => /[^A-Za-z0-9]/.test(p) },
];

/** Boolean shorthand: does the password pass every rule? */
export function isPasswordStrong(pw: string): boolean {
  return PASSWORD_CHECKS.every((check) => check.test(pw));
}

/** Detailed result — the list of rule labels the password is currently failing. */
export function validatePassword(pw: string): { valid: boolean; failedChecks: string[] } {
  const failedChecks = PASSWORD_CHECKS.filter((c) => !c.test(pw)).map((c) => c.label());
  return { valid: failedChecks.length === 0, failedChecks };
}
